import { useState } from "react";

import { PDFDocument }
from "pdf-lib";

import { saveAs }
from "file-saver";

import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import SEO from "../../components/SEO";

import PdfUploader
from "../../components/pdf/PdfUploader";

export default function MergePdf() {

  const [files, setFiles] =
    useState([]);

  const [loading, setLoading] =
    useState(false);

  const [merged, setMerged] =
    useState(false);

  /* HANDLE FILES */

  const handleDrop =
    (acceptedFiles) => {

      const pdfFiles =
        acceptedFiles.filter(
          (file) =>
            file.type ===
            "application/pdf"
        );

      setFiles(pdfFiles);
    };

  /* REMOVE FILE */

  const removeFile =
    (indexToRemove) => {

      setFiles(
        files.filter(
          (_, index) =>
            index !== indexToRemove
        )
      );
    };

  /* FORMAT SIZE */

  const formatSize =
    (bytes) => {

      return (
        (bytes / 1024 / 1024)
          .toFixed(2) + " MB"
      );
    };

  /* MERGE */

  const mergePDFs =
    async () => {

      if (files.length < 2) {

        alert(
          "Please upload at least 2 PDF files"
        );

        return;
      }

      try {

        setLoading(true);

        const mergedPdf =
          await PDFDocument.create();

        for (const file of files) {

          const bytes =
            await file.arrayBuffer();

          const pdf =
            await PDFDocument.load(
              bytes
            );

          const copiedPages =
            await mergedPdf.copyPages(
              pdf,
              pdf.getPageIndices()
            );

          copiedPages.forEach(
            (page) => {

              mergedPdf.addPage(
                page
              );
            }
          );
        }

        const mergedBytes =
          await mergedPdf.save();

        const blob =
          new Blob(
            [mergedBytes],
            {
              type:
                "application/pdf",
            }
          );

        saveAs(
          blob,
          "merged-pdf-tools-center.pdf"
        );

        setMerged(true);

      } catch (error) {

        console.error(error);

        alert(
          "Error merging PDFs"
        );

      } finally {

        setLoading(false);
      }
    };

  return (
    <>
      <SEO
        title="Merge PDF Online Free"
        description="Merge multiple PDF files into one PDF instantly online for free. Secure browser-based PDF merger."
        keywords="merge pdf, combine pdf, pdf merger online, free pdf merge"
        url="https://online-tools-center.vercel.app/merge-pdf"
      />

      <Navbar />

      <div className="container py-4">

        <div className="row justify-content-center">

          <div className="col-lg-10">

            <div className="card shadow-lg border-0 rounded-4 p-4">

              {/* HERO */}

              <div className="text-center mb-5">

                <h1 className="fw-bold mb-3">
                  Merge PDF Files
                </h1>

                <p className="text-muted">
                  Combine multiple PDF
                  files into one document
                  instantly and securely.
                </p>

                <div className="d-flex flex-wrap justify-content-center gap-2 mt-3">

                  <span className="badge bg-light text-dark p-2">
                    ✓ Secure Processing
                  </span>

                  <span className="badge bg-light text-dark p-2">
                    ✓ No Upload Limits
                  </span>

                  <span className="badge bg-light text-dark p-2">
                    ✓ Fast PDF Merge
                  </span>

                  <span className="badge bg-light text-dark p-2">
                    ✓ Free Tool
                  </span>

                </div>

              </div>

              {/* UPLOADER */}

              <div className="mb-4">

                <PdfUploader
                  onDrop={handleDrop}
                />

              </div>

              {/* FILE LIST */}

              {files.length > 0 && (

                <div className="mb-4">

                  <h4 className="fw-bold mb-3">
                    Uploaded PDF Files
                  </h4>

                  <div className="row g-3">

                    {files.map(
                      (file, index) => (

                        <div
                          key={index}
                          className="col-md-6"
                        >

                          <div className="card border-0 bg-light rounded-4 p-3 h-100">

                            <div className="d-flex justify-content-between align-items-start">

                              <div>

                                <h6 className="fw-semibold mb-1">
                                  {file.name}
                                </h6>

                                <small className="text-muted">
                                  {formatSize(
                                    file.size
                                  )}
                                </small>

                              </div>

                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() =>
                                  removeFile(
                                    index
                                  )
                                }
                              >
                                ✕
                              </button>

                            </div>

                          </div>

                        </div>
                      )
                    )}

                  </div>

                </div>

              )}

              {/* ACTION */}

              <div className="text-center">

                <button
                  className="btn btn-primary btn-lg px-5"
                  onClick={mergePDFs}
                  disabled={loading}
                >

                  {loading
                    ? "Merging PDFs..."
                    : "Merge PDFs"}

                </button>

              </div>

              {/* SUCCESS */}

              {merged && (

                <div className="alert alert-success mt-4 text-center">

                  PDF merged successfully
                  and downloaded.

                </div>

              )}

            </div>

            {/* SEO CONTENT */}

            <div className="mt-5">

              <h2 className="fw-bold mb-3">
                What is a PDF Merger?
              </h2>

              <p>
                A PDF merger helps users
                combine multiple PDF files
                into a single document quickly
                and securely without uploading
                files to external servers.
              </p>

              <h2 className="fw-bold mt-5 mb-3">
                Features of Our PDF Merge Tool
              </h2>

              <ul>

                <li>
                  Combine unlimited PDFs
                </li>

                <li>
                  100% browser-based processing
                </li>

                <li>
                  Secure and private
                </li>

                <li>
                  Fast merging speed
                </li>

                <li>
                  Mobile responsive
                </li>

                <li>
                  Free online tool
                </li>

              </ul>

              <h2 className="fw-bold mt-5 mb-3">
                How to Merge PDFs?
              </h2>

              <ol>

                <li>
                  Upload PDF files
                </li>

                <li>
                  Arrange or remove files
                </li>

                <li>
                  Click Merge PDFs
                </li>

                <li>
                  Download merged PDF
                </li>

              </ol>

              <h2 className="fw-bold mt-5 mb-3">
                Frequently Asked Questions
              </h2>

              <div className="mb-4">

                <h5>
                  Is this PDF merger free?
                </h5>

                <p>
                  Yes, this online PDF merger
                  is completely free to use.
                </p>

              </div>

              <div className="mb-4">

                <h5>
                  Are my files secure?
                </h5>

                <p>
                  Yes, all PDF processing
                  happens directly in your
                  browser for maximum privacy.
                </p>

              </div>

              <div className="mb-4">

                <h5>
                  Can I merge multiple PDFs?
                </h5>

                <p>
                  Yes, you can merge multiple
                  PDF files into one document.
                </p>

              </div>

              {/* RELATED TOOLS */}

              <div className="mt-5 p-4 bg-light rounded-4">

                <h3 className="fw-bold mb-3">
                  Related PDF Tools
                </h3>

                <div className="d-flex flex-wrap gap-3">

                  <a
                    href="/split-pdf"
                    className="btn btn-outline-primary"
                  >
                    Split PDF
                  </a>

                  <a
                    href="/image-to-pdf"
                    className="btn btn-outline-primary"
                  >
                    Image to PDF
                  </a>

                  <a
                    href="/qr-generator"
                    className="btn btn-outline-primary"
                  >
                    QR Generator
                  </a>

                </div>

              </div>

            </div>

          </div>

        </div>

      </div>

      <Footer />

    </>
  );
}